if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      // Merkt sich den vom Nutzer eingegebenen Wert und das fokussierte
      // Steuerelement, bevor die Karte neu gerendert wird. Nur aktiv, solange
      // der Nutzer wirklich in dieser Karte arbeitet.
      captureFocusState() {
        if (!this.contains(document.activeElement)) return null;
        const active = document.activeElement;
        return {
          value: this.input?.value,
          buttonName: active.matches('button[name]') ? active.getAttribute('name') : null,
          onInput: active === this.input,
        };
      }

      // Stellt nach dem Re-Render den gemerkten Wert + Fokus wieder her, damit
      // das Mengenfeld bei schnellen Klicks nicht auf den Server-Stand zurückspringt.
      restoreFocusState(state) {
        if (!state) return;
        if (this.input && state.value != null) this.input.value = state.value;
        const target = state.onInput
          ? this.input
          : state.buttonName
          ? this.querySelector(`button[name="${state.buttonName}"]`)
          : null;
        target?.focus();
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      selectProgressIcon() {
        return this.querySelector('.progress-bar-icon');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                const focusState = this.captureFocusState();
                this.innerHTML = sourceQty.innerHTML;
                this.restoreFocusState(focusState);
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');
        this.selectProgressIcon().classList.remove('hidden');

        const ids = Object.keys(items);
        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.text().then((text) => ({ ok: response.ok, text })))
          .then(({ ok, text }) => {
            const parsedState = JSON.parse(text);
            // Backend-Fehler (z. B. nicht genug Bestand) oder fehlende Sections:
            // nicht rendern, sondern die Anzeige zurück auf den echten Stand holen.
            if (!ok || parsedState.status || !parsedState.sections) {
              this.handleUpdateError(parsedState);
              return;
            }
            this.renderSections(parsedState, ids);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch((error) => {
            // Netzwerk-/Parsefehler: ebenfalls auf den echten Warenkorb resynchronisieren.
            console.error('quick-add-bulk: Warenkorb-Update fehlgeschlagen', error);
            this.handleUpdateError();
          })
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.selectProgressIcon().classList.add('hidden');
            this.setRequestStarted(false);
          });
      }

      // Bei fehlgeschlagenem Update die Mengenfelder wieder mit dem echten
      // Warenkorb-Stand abgleichen, damit die Anzeige nicht falsch (zu hoch)
      // stehen bleibt und dem Kunden eine korrekte Menge zeigt.
      handleUpdateError(parsedState) {
        if (parsedState && parsedState.description) {
          console.error('quick-add-bulk:', parsedState.description);
        }
        // Fokus aufheben, damit der Resync den echten Warenkorb-Stand zeigt und
        // nicht den gemerkten Eingabewert (Fokus-Erhaltung). Bei einem Fehler
        // hat die Wahrheit Vorrang – der zurückspringende Wert ist das Feedback.
        if (this.contains(document.activeElement)) document.activeElement.blur();
        this.onCartUpdate();
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        const focusState = this.captureFocusState();
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (section.section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length.length === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        this.restoreFocusState(focusState);

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
